import {
  CompactTimeMaterialResponse,
  EstimateLineItem,
  TimeMaterialResponse,
  TimeMaterialStatus,
  TimeMaterialId,
  ProductStatus,
} from './../../entities/tm/TimeMaterialResponse';
import {
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderShipmentId,
  OrderShipmentRequest,
  OrderShipmentStatus,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId, TimeTrackingInfoResponse, JustAddressReponse, PaymentId } from '@business/entities/organization';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import {
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderEstimateId,
  OrderShippingRequest,
  ReceiveItemRequest,
} from './../../entities/order/OrderResponse';
import { OrderStageId } from './../../entities/order/OrderStageResponse';
import { PoType, PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';
import { TimeMaterialAPI } from '@business/api/timeMaterial';
import { sendEmailTimeTrackingRequest, TimeLogId } from '@business/entities/time_log';
import { UserId } from '@business/entities/account';
import { POStatusID } from '@business/entities/poStatus';
import { ProcessTmInvoiceFormData } from '@modules/organizations/OrganizationPage/Components/Billing/TimeMaterialPaymentModal/TimeMaterialPaymentModal';
import { GeneratePoProductFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/GeneratePoModal/GeneratePoModal';

export class TimeMaterialBloc extends BaseBloc {
  private readonly itemRepo = new Repository<CompactTimeMaterialResponse | TimeMaterialResponse>({
    getItemId: item => item.id,
  });

  readonly orders$ = this.itemRepo.items$;
  readonly selectOrder = this.itemRepo.selectItem;

  onReset() {
    this.itemRepo.reset();
  }

  fetchOrders = (orgId?: OrgId) => {
    return TimeMaterialAPI.fetchOrders(orgId).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrgOrders = (orgId: OrgId) => {
    return TimeMaterialAPI.fetchOrgOrders(orgId).pipe(this.itemRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  searchOrders = (search: string) => {
    return TimeMaterialAPI.searchOrders(search).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  fetchOrder = (id: OrderProductId, fromNgrok = false, isInvoice?: boolean, isPublic = false, withLogo = false) => {
    return TimeMaterialAPI.fetchOrder(id, isInvoice, isPublic, withLogo).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_error') }),
    );
  };

  createOrder = (data: OrderRequest) => {
    return TimeMaterialAPI.createOrder(data).pipe(
      this.itemRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order'),
      }),
    );
  };

  updateEstimate = (id: OrderId, data: OrderRequest) => {
    return TimeMaterialAPI.updateEstimate(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateInvoice = (id: InvoiceId, data: OrderRequest) => {
    return TimeMaterialAPI.updateInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  createInvoice = (id: OrderId, data: OrderRequest) => {
    return TimeMaterialAPI.createInvoice(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateOrder = (id: OrderId, data: Partial<OrderRequest>, isSend = false) => {
    return TimeMaterialAPI.updateOrder(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  deleteOrder = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete order', `Do you really want to remove this order?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteOrder(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order'),
      }),
    );
  };

  updateStage = (
    id: OrderId,
    stageId: OrderStageId,
    deliveryDate?: number,
    closeIt = false,
    sendIt?: boolean,
    emailData?: OrderEmailTemplateRequest,
  ) => {
    return TimeMaterialAPI.updateStage(id, stageId, deliveryDate, closeIt, sendIt, emailData).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  updateStatus = (id: OrderId, status: TimeMaterialStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) => {
    return TimeMaterialAPI.updateStatus(id, status, reason, isSend, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  updateShipping = (id: OrderId, stageId: OrderStageId, data: OrderShippingRequest) => {
    return TimeMaterialAPI.updateShipping(id, stageId, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  acceptEstimate = (id: OrderEstimateId, name: string, email: string) => {
    return showConfirmation(`Accept ${gcLabel('Estimate')}`, `Do you really want to accept this ${gcLabel('estimate')}?`).pipe(
      switchMap(() => TimeMaterialAPI.acceptEstimate(id, name, email)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  createOrderInvoice = (id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) => {
    return TimeMaterialAPI.createOrderInvoice(id, invoiceId, sent, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  sendEstimate = (id: OrderEstimateId, data?: Partial<OrderRequest>, withoutAsking = false) => {
    if (withoutAsking) {
      return TimeMaterialAPI.sendEstimate(id, data).pipe(
        this.itemRepo.ops.upsertOne(item => ({ item })),
        handleMessage({
          type: requestMessage('accept_order'),
        }),
      );
    }
    return showConfirmation(`Send ${gcLabel('Estimate')}`, `Do you really want to send this ${gcLabel('estimate')} to customer?`).pipe(
      switchMap(() => TimeMaterialAPI.sendEstimate(id, data)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('accept_order'),
      }),
    );
  };

  rejectEstimate = (id: OrderEstimateId, reason: string) => {
    return TimeMaterialAPI.rejectEstimate(id, reason).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('reject_order'),
      }),
    );
  };

  sendPurchaseOrder = (id: OrderEstimateId, vendorId: OrderVendorId, data: Partial<OrderEmailTemplateRequest>, poId: string) => {
    return TimeMaterialAPI.sendPurchaseOrder(id, vendorId, data, poId).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('send_purchase_order'),
      }),
    );
  };

  createPurchaseOrder = (id: OrderId, vendorId?: OrderVendorId, address?: JustAddressReponse) => {
    return TimeMaterialAPI.createPurchaseOrder(id, vendorId, address).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  closePurchaseOrder = (poId: PurchaseOrderId) => {
    return showDeleteConfirmation('Cancel Purchase Order', `Do you really want to cancel this purchase order?`, {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => TimeMaterialAPI.closePurchaseOrder(poId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };

  purchaseOrderStatus = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) => {
    return TimeMaterialAPI.purchaseOrderStatus(poId, status, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updatePOStatus = (poId: PurchaseOrderId, status: POStatusID) => {
    return TimeMaterialAPI.updatePoStatus(poId, status).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updatePurchaseOrder = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.updatePurchaseOrder(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };
  saveBillOfLading = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.saveBillOfLading(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  savePickList = (id: PurchaseOrderId, data: Partial<OrderRequest>) => {
    return TimeMaterialAPI.savePickList(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  download = (orderId: OrderId, vendorId: OrderVendorId, filename: string) => {
    return TimeMaterialAPI.download(orderId, vendorId, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadPdf = (purchaseOrderId: PurchaseOrderId, filename: string, type: PoType) => {
    return TimeMaterialAPI.downloadPdf(purchaseOrderId, filename, type).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadLading = (id: PurchaseOrderId, filename: string) => {
    return TimeMaterialAPI.downloadLading(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  downloadPickList = (id: PurchaseOrderId, filename: string) => {
    return TimeMaterialAPI.downloadPickList(id, filename).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };

  createShipment = (poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) => {
    return TimeMaterialAPI.createShipment(poId, status, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  createShipmentWithOutPO = (id: TimeMaterialId, data: Partial<OrderShipmentRequest>, isSend?: boolean) => {
    return TimeMaterialAPI.createShipmentWithOutPO(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('status_update'),
      }),
    );
  };

  updateShipment = (id: OrderShipmentId, data: OrderShippingRequest, isSend?: boolean) => {
    return TimeMaterialAPI.updateShipment(id, data, isSend).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_shipment'),
      }),
    );
  };

  deleteShipment = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Shipment', `Do you really want to remove this shipment?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteShipment(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_shipment'),
      }),
    );
  };

  deleteInvoice = (id: InvoiceId, orderId: OrderId) => {
    return showDeleteConfirmation('Delete Invoice', `Do you really want to remove this invoice?`).pipe(
      switchMap(() => TimeMaterialAPI.deleteInvoice(id, orderId)),
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_invoice'),
      }),
    );
  };

  updateEstimateLineItem = (orderId: TimeMaterialId, id: number, data: Partial<EstimateLineItem>) => {
    return TimeMaterialAPI.updateEstimateLineItem(id, data).pipe(
      this.itemRepo.ops.updateOne(estimate => ({
        id: orderId,
        changes: () => ({ estimate }),
      })),
    );
  };

  updateItemsStatus = (orderId: string, status: ProductStatus, itemIds: number[], date?: number) => {
    return TimeMaterialAPI.updateItemsStatus(orderId, status, itemIds, date).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  updateBulkItemsStatus = (orderId: string, status: ProductStatus, items: Array<{ itemId: number; installedQty: number }>, installedDate: number) => {
    return TimeMaterialAPI.updateBulkItemsStatus(orderId, status, items, installedDate).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  receiveItems = (id: OrderId, data: ReceiveItemRequest[]) => {
    return TimeMaterialAPI.receiveItems(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('tm_receive_items'),
      }),
    );
  };
  getTimeTrackingReport = (id: OrderProductId) => {
    return TimeMaterialAPI.getTimeTrackingReport(id).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  sendEmail = (orderId: string, data: sendEmailTimeTrackingRequest) => {
    return TimeMaterialAPI.sendEmail(orderId, data).pipe(
      handleMessage({
        error: requestMessage('time_tracker_start_error'),
      }),
    );
  };

  selectManager = (orderId: TimeMaterialId, userId: UserId) => {
    return TimeMaterialAPI.selectManager(orderId, userId).pipe(
      this.itemRepo.ops.updateOne(projectManager => ({
        id: orderId,
        changes: () => ({ projectManager }),
      })),
    );
  };

  selectCustomerServiceRep = (orderId: TimeMaterialId, userId: UserId) => {
    return TimeMaterialAPI.selectCustomerServiceRep(orderId, userId).pipe(
      this.itemRepo.ops.updateOne(customerServiceRep => ({
        id: orderId,
        changes: () => ({ customerServiceRep }),
      })),
    );
  };

  updateDates = (orderId: TimeMaterialId, data: Partial<CompactTimeMaterialResponse>) => {
    return TimeMaterialAPI.updateDates(orderId, data).pipe(
      this.itemRepo.ops.updateOne(dueDate => ({
        id: orderId,
        changes: () => ({ ...dueDate }),
      })),
    );
  };

  updateUsers = (id: TimeMaterialId, users: string[]) => {
    return TimeMaterialAPI.updateUsers(id, users).pipe(
      this.itemRepo.ops.updateOne(users => ({
        id,
        changes: () => ({ users }),
      })),
    );
  };

  processTmInvoicePayment = (id: TimeMaterialId, paymentId: PaymentId, amount: number, data: ProcessTmInvoiceFormData) => {
    return TimeMaterialAPI.processTmInvoicePayment(id, paymentId, amount, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_status'),
      }),
    );
  };

  //   stopTimeLog = (id: TimeLogId, data: TimeTrackingInfoResponse) => {
  //     return TimeLogAPI.stopTimeLog(id, data).pipe(
  //       this.itemRepo.ops.upsertOne(item => ({ item })),
  //       handleMessage({
  //         type: requestMessage('stop_time_log'),
  //       }),
  //     );
  //   };

  createBulkPurchaseOrder = (id: OrderId, address: JustAddressReponse, data: GeneratePoProductFormData) => {
    return TimeMaterialAPI.createBulkPurchaseOrder(id, address, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order'),
      }),
    );
  };

  getOrderCanaStatus = (orderId: TimeMaterialId) => {
    return TimeMaterialAPI.getOrderCanaStatus(orderId).pipe(handleMessage({ error: requestMessage('fetch_order_error') }));
  };

  sendCustomerStatement = (orderId: TimeMaterialId) => {
    return TimeMaterialAPI.sendCustomerStatement(orderId).pipe(
      handleMessage({
        type: requestMessage('send_customer_statement'),
      }),
    );
  };

  downloadCustomerStatement = (orderId: TimeMaterialId) => {
    return TimeMaterialAPI.downloadCustomerStatement(orderId).pipe(handleMessage({ error: requestMessage('print_file_error') }));
  };
}
